import { useEffect, useState } from "react";
import { Globe2, Save } from "lucide-react";
import api from "@/lib/crmApi";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const COUNTRIES = [
  ["FR", "France", "+33"], ["BE", "Belgique", "+32"], ["CH", "Suisse", "+41"], ["LU", "Luxembourg", "+352"],
  ["MC", "Monaco", "+377"], ["DE", "Allemagne", "+49"], ["ES", "Espagne", "+34"], ["IT", "Italie", "+39"],
  ["PT", "Portugal", "+351"], ["GB", "Royaume-Uni", "+44"], ["IE", "Irlande", "+353"], ["NL", "Pays-Bas", "+31"],
  ["US", "États-Unis", "+1"], ["CA", "Canada", "+1"], ["MA", "Maroc", "+212"], ["DZ", "Algérie", "+213"],
  ["TN", "Tunisie", "+216"], ["SN", "Sénégal", "+221"], ["CI", "Côte d'Ivoire", "+225"], ["CM", "Cameroun", "+237"],
];

const flag = (iso) => String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

export default function CountrySettings({ isAdmin }) {
  const [allowed, setAllowed] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/twilio/countries").then(({ data }) => setAllowed(data.allowed)).catch(() => setAllowed(["FR"]));
  }, []);

  if (!allowed) return null;

  const toggle = (iso) => {
    if (!isAdmin) return;
    setAllowed((a) => (a.includes(iso) ? a.filter((c) => c !== iso) : [...a, iso]));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/twilio/countries", { allowed });
      setAllowed(data.allowed);
      toast.success(`Pays autorisés enregistrés (${data.allowed.join(", ")})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="country-settings">
      <div className="flex items-center justify-between mb-1">
        <h4 className="font-heading font-semibold text-slate-800 flex items-center gap-2">
          <Globe2 size={16} className="text-indigo-500" /> Pays autorisés
        </h4>
        {isAdmin && (
          <button data-testid="countries-save" onClick={save} disabled={saving}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">
            <Save size={14} /> Enregistrer
          </button>
        )}
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Les appels entrants et sortants depuis / vers les autres pays sont rejetés automatiquement. Par défaut : France.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {COUNTRIES.map(([iso, name, prefix]) => {
          const on = allowed.includes(iso);
          return (
            <button key={iso} data-testid={`country-${iso}`} onClick={() => toggle(iso)} disabled={!isAdmin}
              className={cn("flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors",
                on ? "border-indigo-300 bg-indigo-50 text-indigo-800" : "border-slate-200 text-slate-600 hover:bg-slate-50",
                !isAdmin && "cursor-default opacity-80")}>
              <span>{flag(iso)}</span>
              <span className="flex-1 truncate">{name}</span>
              <span className="text-xs text-slate-400">{prefix}</span>
            </button>
          );
        })}
      </div>
      {!allowed.length && <p className="text-xs text-rose-500 mt-2">Aucun pays sélectionné — la France sera appliquée par défaut.</p>}
    </div>
  );
}
